import { createSchemaField, ExpressionScope, FormProvider, observer, RecordScope } from '@formily/react';
import { batch } from '@formily/reactive';
import { IAny, IAnyObject, isVacuous, mergeWithArrayOverride } from '@yimoka/shared';
import { IFetchListener, IFieldConfig, ISchema, IStore, ListStore } from '@yimoka/store';
import React, { ComponentType, PropsWithChildren, useEffect, useLayoutEffect, useMemo, useState } from 'react';

import { useConfig, useConfigScope } from '../../context/config';
import { SchemaFieldProvider } from '../../context/schema-field';
import { useComponents } from '../../hooks/components';
import { useRoot } from '../../hooks/root';


/** EntitySchema 合并 Store 字段配置、表达式作用域与组件表并渲染 Formily Schema。 */
export const EntitySchema = observer((props: EntitySchemaProps) => {
  const { store, components, scope, schema, schemaKey, children, onError, onSuccess, biz } = props;
  const ctxComponents = useComponents();
  const config = useConfig();
  const globalScope = useConfigScope();
  const root = useRoot();
  // ConfigProvider 显式声明 root（包括 undefined）时必须覆盖全局单例，供不可信表达式建立隔离边界。
  const resolvedRoot = config && Object.prototype.hasOwnProperty.call(config, 'root')
    ? config.root
    : root;
  const { fieldsConfig, form } = store;

  const curSchema = useMemo(() => {
    if (isVacuous(fieldsConfig)) {
      return schema;
    }
    const { definitions = {}, ...args } = schema ?? {};

    const outputSchemas: Record<string, ISchema> = {};
    const newFieldsConfig: Record<string, IFieldConfig> = {};

    Object.entries(fieldsConfig).forEach(([key, value]) => {
      const { 'x-query-schema': querySchema, 'x-output-schema': outputSchema, ...rest } = value;
      if (!isVacuous(outputSchema)) {
        const outputSchemaKey = `__output__${key}`;
        if (typeof outputSchema?.title === 'undefined') {
          outputSchemas[outputSchemaKey] = { ...outputSchema, title: rest.title };
        } else {
          outputSchemas[outputSchemaKey] = outputSchema;
        }
      }
      if (store instanceof ListStore) {
        const { required, 'x-validator': validator, ...querySafeRest } = rest;
        void required;
        void validator;
        const queryFieldConfig = isVacuous(querySchema)
          ? querySafeRest
          : mergeWithArrayOverride({}, querySafeRest, querySchema);
        delete queryFieldConfig.required;
        delete queryFieldConfig['x-validator'];
        newFieldsConfig[key] = queryFieldConfig;
      } else {
        newFieldsConfig[key] = rest;
      }
    });

    const withOutputSchemas = isVacuous(outputSchemas) ? newFieldsConfig : { ...newFieldsConfig, ...outputSchemas };

    return { definitions: typeof definitions === 'object' ? { ...withOutputSchemas, ...definitions } : withOutputSchemas, ...args };
  }, [fieldsConfig, schema, store]);

  const curScope = useMemo(
    () => ({
      ...(globalScope ?? {}),
      $store: store,
      $root: resolvedRoot,
      ...(biz !== undefined && biz !== null ? { $biz: biz } : {}),
      ...scope,
    }),
    [globalScope, store, resolvedRoot, biz, scope],
  );

  const mergedComponents = useMemo(
    () => (components ? { ...ctxComponents, ...components } : ctxComponents),
    [components, ctxComponents],
  );

  const SchemaField = useMemo(
    () => createSchemaField({
      components: mergedComponents,
      scope: curScope,
    }),
    [mergedComponents, curScope],
  );

  /**
   * schemaKey 变化时须先销毁 Field 图再 mount SchemaField。
   * Formily `createField` 在同 form 单例上路径已存在则直接复用，不会更新 title / x-component-props。
   * `forceClear: false` 仅销毁 Field 节点，不删 `form.values`（store.values 同源，须保留用户输入）。
   */
  const [mountedSchemaKey, setMountedSchemaKey] = useState(schemaKey);

  useLayoutEffect(() => {
    if (schemaKey === undefined || schemaKey === null) {
      setMountedSchemaKey(undefined);
      return;
    }
    if (mountedSchemaKey === schemaKey) {
      return;
    }
    // 一次清完字段图，避免尚未销毁的联动读到已移除字段并覆盖需要保留的值。
    batch(() => form.clearFormGraph('*', false));
    setMountedSchemaKey(schemaKey);
  }, [schemaKey, mountedSchemaKey, form]);

  const canRenderSchemaField = schemaKey === undefined || schemaKey === null || mountedSchemaKey === schemaKey;

  useEffect(() => {
    if (onError) {
      store.onFetchError(onError);
    }
    if (onSuccess) {
      store.onFetchSuccess(onSuccess);
    }
    return () => {
      if (onError) {
        store.offFetchError(onError);
      }
      if (onSuccess) {
        store.offFetchSuccess(onSuccess);
      }
    };
  }, [onError, onSuccess, store]);

  return (
    <SchemaFieldProvider value={SchemaField}>
      <FormProvider form={form}>
        <ExpressionScope value={curScope}>
          <RecordScope getRecord={() => store.values}>
            {canRenderSchemaField ? (
              <SchemaField key={schemaKey} schema={curSchema} />
            ) : null}
            {children}
          </RecordScope>
        </ExpressionScope>
      </FormProvider>
    </SchemaFieldProvider>
  );
});

export type EntitySchemaProps<
  V extends object = IAnyObject,
  R extends object = IAnyObject,
  B extends object = IAnyObject,
> = PropsWithChildren<{
  store: IStore<V, R>;
  /** 页面 Biz 编排实例，注入 schema scope 为 `$biz` */
  biz?: B;
  components?: Record<string, ComponentType<IAny>>;
  scope?: IAnyObject;
  schema?: ISchema;
  /**
   * 强制 SchemaField remount 的 key（如 i18n `useSchemaI18nKey()`）。
   * 同一 form 实例下 React key 不足以更新 Field 元数据，须配合内部 `clearFormGraph`。
   */
  schemaKey?: React.Key;
  onError?: IFetchListener;
  onSuccess?: IFetchListener;
}>
